import { agentControllerRefSchema } from "../daemon/agent-controller-ref.js";
import { NEVER, array, boolean, coerce, discriminatedUnion, email, enum as enumFactory, int, iso, lazySchema, literal, null as nullFactory, number, strictObject, string, union, unknown } from "../../lazy-zod.js";
import { MAX_TEXT_LENGTH } from "../protocol/constants.js";
import { agentProfileIdSchema, openMeldRuntimeProfileIdSchema, profileSchema, profileWorkingDirectorySchema, profileWorkspaceModeSchema } from "../protocol/profile.js";
import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
import { authProfileViewSchema } from "./profiles.js";
import { spacePasswordSchema } from "./space-password.js";
import { computerSetupModeSchema } from "../../navigation-action.js";
import { spaceThreadContextSchema } from "../space-mesh/space-thread.js";
import { computerCareObservationSchema } from "../daemon/computer-care.js";
import { spaceMessageFileReferencesSchema } from "./space-files.js";
import { agentTaskCompletionConditionSchema, agentTaskCompletionObservationSchema, agentTaskHandleSchema, agentTaskTerminalOutcomeSchema, controllerAgentTaskHandleSchema, spaceWakeAgentTaskHandleSchema } from "../runtime/agent-task-coordination.js";
                                              
import { prepareReadinessRuntimeIdSchema } from "./prepare-readiness.js";
import { currentLocalAgentInstallationsResponseSchema, currentLocalRuntimeReplyReadinessResponseSchema, requestCurrentOpenMeldToolsUpdateResponseSchema, selectCurrentLocalAgentInstallationResponseSchema, setCurrentAgentProfileWorkspaceResponseSchema, setCurrentOpenMeldToolsMaintenanceResponseSchema } from "./local-runtimes.js";
import { peopleComputerTargetSchema, peopleComputersResponseSchema } from "./people-computers.js";
import { slackMessagePermalinkSchema, slackThreadPublicationPermalinkSchema } from "../runtime/message-channel.js";
import { signalEnvelopeSchema, signalTextEnvelopeSchema } from "../protocol/envelope.js";
import { centerAgentToolSpeakerSchema } from "./center-agent-speaker.js";
import { centerAgentModelChoiceSchema, centerAgentModelPreferenceSchema, centerAgentModelSummarySchema } from "../runtime/center-agent-model.js";
import { spaceAccessModeSchema, spaceAccessModeStateSchema } from "./space-access-mode.js";
import { spaceMetaSchema } from "./space-meta.js";
import { remoteAgentSlackSearchAuthorizationSchema } from "../runtime/remote-agent-turn-envelope.js";
import { agentProfileBindingAgentControllerCollaborationModeSchema, agentProfileBindingAgentControllerPermissionModeSchema, agentProfileBindingOpenCodePermissionSchema, agentProfileCreationPreferenceSelectionSchema, ensureAgentProfilePrimaryBindingResponseSchema, listAgentProfileCreationDefaultsResponseSchema } from "./agent-profile-binding.js";
import { getAgentExecutionReadinessResponseSchema } from "./agent-execution-readiness.js";
import { connectorConnectionNameSchema, connectorConnectionScopeSchema, connectorServiceSchema } from "./connector-connections.js";
import { organizationComputersResponseSchema } from "./organization-computers.js";
import { computerFileAccessSummarySchema, ownerComputerDiagnosticsSchema, ownerComputerLocalRuntimesSchema } from "./owner-computer-diagnostics.js";
import { agentControllerTaskLifecycleEventSourceSchema } from "./runtime-reporting.js";
import { CENTER_AGENT_SLACK_FILE_MAX_BASE64_CHARACTERS, slackFileMetadataSchema } from "./slack-channel-reads.js";
import { postSpaceMembersResponseSchema, removeSpaceMembersResponseSchema } from "./space-members.js";
import { spaceBootstrapResponseSchema } from "./space-bootstrap.js";
import { createSpaceBodySchema } from "./space-create.js";
import { spaceDispatchTraceAttemptStatusSchema, spaceDispatchTraceDispatchStatusSchema } from "./space-dispatch-trace.js";
//#region src/schemas/http/center-agent.ts
const ISO_DATE_TIME_SCHEMA = iso.datetime({ offset: true });
const trimmedNonEmptyStringSchema = string().trim().min(1);
const nullableTrimmedStringSchema = union([trimmedNonEmptyStringSchema, nullFactory()]);
const nullableIsoDateTimeSchema = union([ISO_DATE_TIME_SCHEMA, nullFactory()]);
const nullableDiagnosticTextSchema = union([trimmedNonEmptyStringSchema.max(512), nullFactory()]);
const CENTER_AGENT_REFERENCE_OVERLAP_MESSAGE = "A profile cannot be both a Wake target and a Reference target.";
function findCenterAgentReferenceOverlapIndex(input) {
	const executableProfileIds = new Set(input.activationTargets);
	if (input.fallbackAgentProfileId) executableProfileIds.add(input.fallbackAgentProfileId);
	return input.referenceTargets?.findIndex((profileId) => executableProfileIds.has(profileId));
}
const centerAgentActionSchema = enumFactory([
	"create_space",
	"create_agent_profile",
	"enable_agent_profile",
	"invite_member",
	"carry_over_binding_authorization",
	"designate_remote_center_agent",
	"authorize_remote_center_agent_binding",
	"revoke_center_agent",
	"pause_center_agent",
	"resume_center_agent",
	"connect_slack_workspace",
	"move_slack_workspace",
	"disconnect_slack_workspace",
	"disconnect_message_channel_identity_link",
	"replace_message_channel_identity_link",
	"start_shared_app_authorization",
	"disconnect_shared_app",
	"add_members_to_space",
	"add_agent_profiles_to_space",
	"post_slack_channel_message",
	"post_slack_file",
	"list_slack_channels",
	"read_slack_channel_members",
	"read_slack_channel_history",
	"read_slack_file",
	"read_channel_file",
	"post_channel_file",
	"search_slack_messages",
	"post_space_message",
	"remove_agent_profiles_from_space",
	"read_org_directory",
	"read_member_computers",
	"read_member_openmeld_service_status",
	"read_slack_workspace_directory",
	"read_org_documents",
	"read_org_skills",
	"preview_org_skill",
	"install_org_skill",
	"create_org_document_folder",
	"rename_org_document_folder",
	"move_org_document",
	"save_org_document",
	"read_org_space_directory",
	"read_space_context",
	"read_dispatch_history",
	"read_projects",
	"create_project",
	"update_project",
	"set_agent_profile_default_project",
	"prepare_remove_project",
	"remove_project",
	"update_space_settings",
	"wake_agent_profiles_in_space",
	"read_agent_tasks",
	"watch_agent_task",
	"draft_org_document",
	"research_member",
	"execute_connector_action",
	"complete_connector_action",
	"fail_connector_action",
	"record_connector_action_outcome_unknown",
	"receive_local_agent_request",
	"read_center_agent_context",
	"load_center_agent_context",
	"create_routine",
	"update_routine",
	"pause_routine",
	"resume_routine",
	"delete_routine",
	"set_working_language",
	"observe_working_language"
]);
const centerAgentConnectorActionAuditOutcomeSchema = enumFactory([
	"attempted",
	"completed",
	"failed",
	"outcome_unknown"
]);
const centerAgentRecordConnectorActionAuditBodySchema = strictObject({
	connectionName: connectorConnectionNameSchema.optional(),
	actionId: trimmedNonEmptyStringSchema.max(160),
	operationId: trimmedNonEmptyStringSchema.max(256),
	outcome: centerAgentConnectorActionAuditOutcomeSchema,
	service: connectorServiceSchema,
	speakerMember: centerAgentToolSpeakerSchema
});
const centerAgentResourceSchema = strictObject({
	type: trimmedNonEmptyStringSchema,
	id: trimmedNonEmptyStringSchema
});
const centerAgentOnBehalfOfInputSchema = strictObject({ profileId: openMeldRuntimeProfileIdSchema });
const centerAgentAttributionSchema = strictObject({
	actorProfileId: agentProfileIdSchema,
	onBehalfOf: strictObject({
		profileId: openMeldRuntimeProfileIdSchema,
		userId: nullableTrimmedStringSchema,
		displayName: trimmedNonEmptyStringSchema
	}).nullable()
});
const centerAgentRecordConnectorActionAuditResponseSchema = strictObject({
	attribution: centerAgentAttributionSchema,
	recorded: literal(true)
});
const centerAgentConnectorActionOperationStatusSchema = enumFactory([
	"pending",
	"completed",
	"failed",
	"outcome_unknown"
]);
const centerAgentConnectorActionExecutionConnectionSchema = strictObject({
	connectionName: connectorConnectionNameSchema,
	ownerDisplayName: trimmedNonEmptyStringSchema,
	ownerUserId: trimmedNonEmptyStringSchema,
	scope: connectorConnectionScopeSchema,
	service: connectorServiceSchema
});
const centerAgentReadConnectorActionOperationBodySchema = strictObject({
	actionId: trimmedNonEmptyStringSchema.max(160),
	operationId: trimmedNonEmptyStringSchema.max(256),
	service: connectorServiceSchema,
	speakerMember: centerAgentToolSpeakerSchema
});
const centerAgentReadConnectorActionOperationResponseSchema = strictObject({ operation: strictObject({
	actionId: trimmedNonEmptyStringSchema.max(160),
	connectionName: connectorConnectionNameSchema,
	executionConnection: centerAgentConnectorActionExecutionConnectionSchema.nullable().optional(),
	operationId: trimmedNonEmptyStringSchema.max(256),
	service: connectorServiceSchema,
	status: centerAgentConnectorActionOperationStatusSchema
}).superRefine((value, context) => {
	if (value.executionConnection && (value.executionConnection.connectionName !== value.connectionName || value.executionConnection.service !== value.service)) context.addIssue({
		code: "custom",
		message: "executionConnection must match the connector action operation.",
		path: ["executionConnection"]
	});
}).nullable() });
const centerAgentRecordConnectorActionOperationBodySchema = strictObject({
	actionId: trimmedNonEmptyStringSchema.max(160),
	effect: enumFactory([
		"read",
		"write",
		"destructive",
		"unspecified"
	]).optional(),
	connectionName: connectorConnectionNameSchema,
	operationId: trimmedNonEmptyStringSchema.max(256),
	outcome: centerAgentConnectorActionAuditOutcomeSchema,
	providerExecutionId: nullableTrimmedStringSchema,
	requestSignature: string().regex(/^[0-9a-f]{64}$/),
	service: connectorServiceSchema,
	speakerMember: centerAgentToolSpeakerSchema
}).superRefine((value, context) => {
	if (value.outcome === "attempted" && value.providerExecutionId !== null) context.addIssue({
		code: "custom",
		message: "providerExecutionId must be null while claiming a connector action operation.",
		path: ["providerExecutionId"]
	});
});
const centerAgentRecordConnectorActionOperationResponseSchema = strictObject({
	attribution: centerAgentAttributionSchema,
	executionConnection: centerAgentConnectorActionExecutionConnectionSchema.nullable().optional(),
	operationId: trimmedNonEmptyStringSchema.max(256),
	providerExecutionId: nullableTrimmedStringSchema,
	recorded: literal(true),
	replayed: boolean(),
	status: centerAgentConnectorActionOperationStatusSchema
});
const centerAgentDesignationSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	agentProfileId: agentProfileIdSchema,
	duty: nullFactory(),
	designatedBy: trimmedNonEmptyStringSchema.nullable(),
	createdAt: ISO_DATE_TIME_SCHEMA
});
const centerAgentModelConfigurationResponseSchema = strictObject({
	canManage: boolean(),
	choices: array(centerAgentModelChoiceSchema).min(1),
	configurationRevision: number().int().nonnegative(),
	effectiveModel: centerAgentModelSummarySchema,
	organizationId: trimmedNonEmptyStringSchema,
	preference: centerAgentModelPreferenceSchema
});
const updateCenterAgentModelBodySchema = strictObject({
	expectedConfigurationRevision: number().int().nonnegative(),
	preference: centerAgentModelPreferenceSchema
});
const updateCenterAgentModelResponseSchema = centerAgentModelConfigurationResponseSchema.extend({ changed: boolean() });
const centerAgentRemoteRuntimeAvailabilityReasonCodeSchema = enumFactory([
	"remote_runtime_disabled",
	"organization_blocked",
	"remote_runtime_policy_unavailable"
]);
const centerAgentRemoteRuntimeAvailabilitySchema = strictObject({
	selectable: boolean(),
	reasonCode: centerAgentRemoteRuntimeAvailabilityReasonCodeSchema.optional()
});
const centerAgentSlackWorkspaceConnectionSchema = strictObject({
	connected: boolean(),
	directMessagesReady: boolean().optional(),
	slackTeamId: nullableTrimmedStringSchema,
	workspaceName: nullableTrimmedStringSchema
});
const getCenterAgentDesignationResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	designation: centerAgentDesignationSchema.nullable(),
	remoteRuntimeAvailability: centerAgentRemoteRuntimeAvailabilitySchema.optional(),
	slackConnection: centerAgentSlackWorkspaceConnectionSchema.optional()
});
const centerAgentSlackWorkspaceConnectBodySchema = strictObject({ slackTeamId: trimmedNonEmptyStringSchema });
const centerAgentSlackWorkspaceConnectionResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	slackConnection: centerAgentSlackWorkspaceConnectionSchema
});
const centerAgentSlackWorkspaceDisconnectResponseSchema = strictObject({
	disconnected: boolean(),
	organizationId: trimmedNonEmptyStringSchema,
	slackConnection: centerAgentSlackWorkspaceConnectionSchema
});
const slackIdentityLinkTokenSchema = string().trim().regex(/^slacklink_[0-9a-f]{64}$/);
const slackIdentityLinkPreviewBodySchema = strictObject({ token: slackIdentityLinkTokenSchema });
const slackIdentityLinkPreviewResponseSchema = strictObject({
	alreadyMember: boolean().default(true),
	continuesRequest: boolean().default(true),
	expiresAt: ISO_DATE_TIME_SCHEMA,
	organizationId: trimmedNonEmptyStringSchema,
	organizationName: trimmedNonEmptyStringSchema,
	slackAccountEmail: email().nullable().default(null),
	slackDisplayName: trimmedNonEmptyStringSchema,
	slackTeamId: trimmedNonEmptyStringSchema,
	slackUserId: trimmedNonEmptyStringSchema,
	slackWorkspaceLabel: trimmedNonEmptyStringSchema,
	openMeldAccountEmail: trimmedNonEmptyStringSchema,
	replacesExistingLink: boolean().default(false)
});
const slackIdentityLinkConsumeBodySchema = strictObject({
	includeReturnTarget: literal(true).optional(),
	token: slackIdentityLinkTokenSchema
});
const slackIdentityLinkConsumeResponseSchema = strictObject({
	continuationRetryable: boolean().default(false),
	continued: boolean().default(false),
	joinedOrganization: boolean().default(false),
	linked: literal(true),
	openMeldProfileId: trimmedNonEmptyStringSchema.nullable().default(null),
	organizationId: trimmedNonEmptyStringSchema,
	organizationName: trimmedNonEmptyStringSchema,
	organizationSlug: trimmedNonEmptyStringSchema.nullable().default(null),
	slackDisplayName: trimmedNonEmptyStringSchema,
	slackTeamId: trimmedNonEmptyStringSchema,
	slackThreadPermalink: slackMessagePermalinkSchema.nullable().optional(),
	slackUserId: trimmedNonEmptyStringSchema,
	replacedExistingLink: boolean().default(false),
	openMeldUserId: trimmedNonEmptyStringSchema
});
const ensureSlackWorkspaceMemberOrganizationMembershipRpcInputSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	userId: trimmedNonEmptyStringSchema
});
const ensureSlackWorkspaceMemberOrganizationMembershipRpcResponseSchema = discriminatedUnion("ok", [strictObject({
	alreadyMember: boolean(),
	ok: literal(true),
	organizationId: trimmedNonEmptyStringSchema,
	organizationName: trimmedNonEmptyStringSchema,
	organizationSlug: trimmedNonEmptyStringSchema,
	role: enumFactory([
		"owner",
		"admin",
		"member"
	])
}), strictObject({
	code: enumFactory([
		"slack.workspace_member_enrollment.account_unavailable",
		"slack.workspace_member_enrollment.invalid_input",
		"slack.workspace_member_enrollment.email_verification_required",
		"slack.workspace_member_enrollment.organization_inactive",
		"slack.workspace_member_enrollment.personal_organization"
	]),
	message: trimmedNonEmptyStringSchema,
	ok: literal(false)
})]);
const ensureMessageChannelMemberOrganizationMembershipRpcInputSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	provider: enumFactory([
		"feishu",
		"lark",
		"slack"
	]),
	userId: trimmedNonEmptyStringSchema
});
const ensureMessageChannelMemberOrganizationMembershipRpcResponseSchema = discriminatedUnion("ok", [strictObject({
	alreadyMember: boolean(),
	ok: literal(true),
	organizationId: trimmedNonEmptyStringSchema,
	organizationName: trimmedNonEmptyStringSchema,
	organizationSlug: trimmedNonEmptyStringSchema,
	role: enumFactory([
		"owner",
		"admin",
		"member"
	])
}), strictObject({
	code: enumFactory([
		"message_channel.member_enrollment.account_unavailable",
		"message_channel.member_enrollment.email_verification_required",
		"message_channel.member_enrollment.invalid_input",
		"message_channel.member_enrollment.organization_inactive",
		"message_channel.member_enrollment.personal_organization"
	]),
	message: trimmedNonEmptyStringSchema,
	ok: literal(false)
})]);
const designateCenterAgentBodySchema = strictObject({
	agentProfileId: agentProfileIdSchema.optional(),
	runtime: enumFactory(["self_hosted", "remote_agent"]).default("self_hosted")
});
const designateCenterAgentResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	designation: centerAgentDesignationSchema,
	status: enumFactory([
		"designated",
		"replaced",
		"unchanged"
	])
});
const centerAgentOperatingStateSchema = enumFactory(["active", "paused"]);
const getCenterAgentOperatingStateResponseSchema = strictObject({
	canChangeOperatingState: boolean(),
	operatingState: centerAgentOperatingStateSchema,
	organizationId: trimmedNonEmptyStringSchema,
	pausedAt: nullableIsoDateTimeSchema
});
const updateCenterAgentOperatingStateBodySchema = strictObject({ operatingState: centerAgentOperatingStateSchema });
const updateCenterAgentOperatingStateResponseSchema = getCenterAgentOperatingStateResponseSchema.extend({
	affectedTaskCount: number().int().nonnegative(),
	changed: boolean()
});
const centerAgentUpdateProfileBodySchema = strictObject({ profileName: trimmedNonEmptyStringSchema });
const centerAgentUpdateProfileResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	profile: authProfileViewSchema
});
const centerAgentAuditJournalEntrySchema = strictObject({
	id: trimmedNonEmptyStringSchema,
	organizationId: trimmedNonEmptyStringSchema,
	actorProfileId: openMeldRuntimeProfileIdSchema,
	action: centerAgentActionSchema,
	onBehalfOf: strictObject({
		profileId: openMeldRuntimeProfileIdSchema,
		userId: nullableTrimmedStringSchema,
		displayName: trimmedNonEmptyStringSchema
	}).nullable(),
	interactionSource: strictObject({
		client: trimmedNonEmptyStringSchema.max(80),
		kind: literal("local_agent")
	}).optional(),
	contextManifest: strictObject({
		estimatedTokens: number().int().nonnegative(),
		includedMessages: number().int().nonnegative(),
		truncated: boolean()
	}).optional(),
	contextLoad: strictObject({
		estimatedTokens: number().int().nonnegative(),
		includedMessages: number().int().min(1).max(50),
		manifestId: trimmedNonEmptyStringSchema
	}).optional(),
	resource: centerAgentResourceSchema.nullable(),
	sourceSpaceId: trimmedNonEmptyStringSchema.nullable().optional(),
	createdAt: ISO_DATE_TIME_SCHEMA
});
const listCenterAgentAuditJournalQuerySchema = strictObject({
	actionSet: enumFactory([
		"v2",
		"v3",
		"v4",
		"v5",
		"v6",
		"v7",
		"v8",
		"v9",
		"v10",
		"v11",
		"v12",
		"v13",
		"v14",
		"v15",
		"v16",
		"v17",
		"v18",
		"v19",
		"v20",
		"v21",
		"v22"
	]).optional(),
	limit: coerce.number().int().min(1).max(200).optional()
});
const listCenterAgentAuditJournalResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	entries: array(centerAgentAuditJournalEntrySchema)
});
const centerAgentCreateSpaceBodySchema = strictObject({
	name: unknown().optional(),
	purpose: unknown().optional(),
	guide: unknown().optional(),
	visibility: unknown().optional(),
	accessMode: unknown().optional(),
	password: unknown().optional(),
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional()
}).transform((value, ctx) => {
	const { onBehalfOf, ...spaceBody } = value;
	const parsedSpaceBody = createSpaceBodySchema.safeParse(spaceBody);
	if (!parsedSpaceBody.success) {
		ctx.addIssue({
			code: "custom",
			message: parsedSpaceBody.error.issues[0]?.message ?? "Invalid Center Agent Space payload"
		});
		return NEVER;
	}
	return {
		...parsedSpaceBody.data,
		...onBehalfOf === void 0 ? {} : { onBehalfOf }
	};
});
const centerAgentCreateAgentProfileBodySchema = strictObject({
	profileName: trimmedNonEmptyStringSchema,
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional()
});
const centerAgentEnableAgentProfileBodySchema = strictObject({ onBehalfOf: centerAgentOnBehalfOfInputSchema.optional() });
const centerAgentAddAgentProfilesToSpaceBodySchema = strictObject({
	agentProfileIds: array(agentProfileIdSchema).min(1).max(100),
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional(),
	password: trimmedNonEmptyStringSchema.optional(),
	spaceId: trimmedNonEmptyStringSchema
});
const centerAgentAddMembersToSpaceBodySchema = strictObject({
	memberUserIds: array(trimmedNonEmptyStringSchema).min(1).max(100),
	spaceId: trimmedNonEmptyStringSchema,
	speakerMember: centerAgentToolSpeakerSchema
});
const centerAgentRemoveAgentProfilesFromSpaceBodySchema = strictObject({
	agentProfileIds: array(agentProfileIdSchema).min(1).max(100),
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional(),
	spaceId: trimmedNonEmptyStringSchema
});
const centerAgentSpaceContextScopeSchema = enumFactory([
	"source",
	"thread",
	"delta",
	"history"
]);
const centerAgentSpaceContextAnchorByScope = {
	delta: "afterSignalId",
	history: null,
	source: "sourceSignalId",
	thread: "threadId"
};
const centerAgentReadSpaceContextBodySchema = strictObject({
	afterSignalId: trimmedNonEmptyStringSchema.optional(),
	limit: number().int().min(1).max(100).optional(),
	requestedAddressingModel: literal("wake_reference_mention").optional(),
	scope: centerAgentSpaceContextScopeSchema.optional(),
	sourceSignalId: trimmedNonEmptyStringSchema.optional(),
	spaceId: trimmedNonEmptyStringSchema,
	threadId: trimmedNonEmptyStringSchema.optional()
}).superRefine((value, context) => {
	const requiredAnchor = value.scope ? centerAgentSpaceContextAnchorByScope[value.scope] : null;
	if (requiredAnchor && !value[requiredAnchor]) context.addIssue({
		code: "custom",
		message: `${requiredAnchor} is required for ${value.scope} context.`,
		path: [requiredAnchor]
	});
	for (const anchor of [
		"sourceSignalId",
		"threadId",
		"afterSignalId"
	]) if (value[anchor] && anchor !== requiredAnchor) context.addIssue({
		code: "custom",
		message: `${anchor} is not valid for ${value.scope ?? "legacy"} context.`,
		path: [anchor]
	});
});
const centerAgentReadDispatchHistoryBodySchema = strictObject({
	dispatchId: trimmedNonEmptyStringSchema.optional(),
	limit: number().int().min(1).max(20).optional(),
	spaceId: trimmedNonEmptyStringSchema,
	targetProfileId: trimmedNonEmptyStringSchema.optional()
});
const centerAgentPostSpaceMessageBodySchema = strictObject({
	files: spaceMessageFileReferencesSchema.optional(),
	mentionProfileIds: array(openMeldRuntimeProfileIdSchema).max(25).optional(),
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional(),
	referenceProfileIds: array(openMeldRuntimeProfileIdSchema).max(25).optional(),
	spaceId: trimmedNonEmptyStringSchema,
	text: string().trim().min(1).max(MAX_TEXT_LENGTH)
}).superRefine((value, context) => {
	const referenceProfileIds = new Set(value.referenceProfileIds ?? []);
	const overlappingMentionIndex = value.mentionProfileIds?.findIndex((profileId) => referenceProfileIds.has(profileId));
	if (overlappingMentionIndex !== void 0 && overlappingMentionIndex >= 0) context.addIssue({
		code: "custom",
		message: "A profile cannot be both a Human Mention and a Reference target.",
		path: ["mentionProfileIds", overlappingMentionIndex]
	});
});
const CENTER_AGENT_PUBLICATION_OPERATION_ID_MAX_CHARS = 256;
const centerAgentRemoteAgentExecutionPublicationSourceSchema = strictObject({
	executionId: trimmedNonEmptyStringSchema.max(512),
	kind: literal("remote_agent_execution"),
	operationId: trimmedNonEmptyStringSchema.max(256).optional()
});
const centerAgentPublicationSourceSchema = discriminatedUnion("kind", [
	strictObject({
		documentId: trimmedNonEmptyStringSchema.max(256),
		kind: literal("organization_document")
	}),
	centerAgentRemoteAgentExecutionPublicationSourceSchema,
	strictObject({
		kind: literal("routine_run"),
		runId: trimmedNonEmptyStringSchema.max(256)
	})
]);
const centerAgentSlackMessageCommonBodyFields = {
	speakerMember: centerAgentToolSpeakerSchema,
	text: string().trim().min(1).max(MAX_TEXT_LENGTH)
};
const centerAgentPostSlackChannelMessageBodySchema = strictObject({
	...centerAgentSlackMessageCommonBodyFields,
	channelName: trimmedNonEmptyStringSchema.max(80),
	publicationSource: centerAgentPublicationSourceSchema.optional()
});
const centerAgentPostSlackThreadMessageBodySchema = union([strictObject({
	...centerAgentSlackMessageCommonBodyFields,
	publicationSource: centerAgentRemoteAgentExecutionPublicationSourceSchema,
	threadPermalink: slackThreadPublicationPermalinkSchema
}), strictObject({
	...centerAgentSlackMessageCommonBodyFields,
	channelName: trimmedNonEmptyStringSchema.max(80),
	publicationSource: centerAgentRemoteAgentExecutionPublicationSourceSchema,
	threadTs: string().trim().regex(/^\d+\.\d+$/).max(32)
})]);
const centerAgentSlackFilePublicationCommonFields = {
	contentBase64: string().max(CENTER_AGENT_SLACK_FILE_MAX_BASE64_CHARACTERS).regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/u),
	contentSha256: string().regex(/^[0-9a-f]{64}$/u),
	fileName: trimmedNonEmptyStringSchema.max(255),
	initialComment: string().trim().min(1).max(MAX_TEXT_LENGTH).optional(),
	publicationSource: centerAgentRemoteAgentExecutionPublicationSourceSchema,
	speakerMember: centerAgentToolSpeakerSchema,
	title: trimmedNonEmptyStringSchema.max(255).optional()
};
const centerAgentPostSlackFileBodySchema = union([
	strictObject({
		...centerAgentSlackFilePublicationCommonFields,
		currentThread: literal(true)
	}),
	strictObject({
		...centerAgentSlackFilePublicationCommonFields,
		channelName: trimmedNonEmptyStringSchema.max(80)
	}),
	strictObject({
		...centerAgentSlackFilePublicationCommonFields,
		threadPermalink: slackThreadPublicationPermalinkSchema
	})
]);
const centerAgentSearchSlackMessagesBodySchema = strictObject({
	includeBots: boolean().optional(),
	query: trimmedNonEmptyStringSchema.max(500),
	searchAuthorization: remoteAgentSlackSearchAuthorizationSchema,
	speakerMember: centerAgentToolSpeakerSchema
});
const CENTER_AGENT_DELEGATION_COMPLETION_MODES = [
	"child_reports_directly",
	"resume_parent_when_all_terminal",
	"resume_parent_when_first_success",
	"resume_parent_on_failure",
	"resume_parent_on_each_terminal"
];
const centerAgentDelegationCompletionModeSchema = enumFactory(CENTER_AGENT_DELEGATION_COMPLETION_MODES);
const centerAgentWakeAgentProfilesInSpaceBodySchema = strictObject({
	agentProfileIds: array(agentProfileIdSchema).min(1).max(25),
	completionMode: centerAgentDelegationCompletionModeSchema.optional(),
	fallbackAgentProfileId: agentProfileIdSchema.optional(),
	mentionProfileIds: array(openMeldRuntimeProfileIdSchema).max(25).optional(),
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional(),
	referenceProfileIds: array(openMeldRuntimeProfileIdSchema).max(25).optional(),
	spaceId: trimmedNonEmptyStringSchema,
	text: string().trim().min(1).max(MAX_TEXT_LENGTH)
}).superRefine((value, context) => {
	const otherAddressingProfileIds = /* @__PURE__ */ new Set([
		...value.agentProfileIds,
		...value.fallbackAgentProfileId ? [value.fallbackAgentProfileId] : [],
		...value.referenceProfileIds ?? []
	]);
	const overlappingMentionIndex = value.mentionProfileIds?.findIndex((profileId) => otherAddressingProfileIds.has(profileId));
	if (overlappingMentionIndex !== void 0 && overlappingMentionIndex >= 0) context.addIssue({
		code: "custom",
		message: "A profile cannot be both a Human Mention and another addressing target.",
		path: ["mentionProfileIds", overlappingMentionIndex]
	});
	const overlappingReferenceIndex = findCenterAgentReferenceOverlapIndex({
		activationTargets: value.agentProfileIds,
		fallbackAgentProfileId: value.fallbackAgentProfileId,
		referenceTargets: value.referenceProfileIds
	});
	if (overlappingReferenceIndex !== void 0 && overlappingReferenceIndex >= 0) context.addIssue({
		code: "custom",
		message: CENTER_AGENT_REFERENCE_OVERLAP_MESSAGE,
		path: ["referenceProfileIds", overlappingReferenceIndex]
	});
	if (!value.fallbackAgentProfileId) return;
	if (!value.completionMode) context.addIssue({
		code: "custom",
		message: "completionMode is required when fallbackAgentProfileId is provided.",
		path: ["completionMode"]
	});
	const primaryProfileIds = new Set(value.agentProfileIds);
	if (primaryProfileIds.has(value.fallbackAgentProfileId)) context.addIssue({
		code: "custom",
		message: "fallbackAgentProfileId must differ from every primary Agent.",
		path: ["fallbackAgentProfileId"]
	});
	if (primaryProfileIds.size !== 1) context.addIssue({
		code: "custom",
		message: "Fallback activation currently requires exactly one primary Agent.",
		path: ["agentProfileIds"]
	});
	if (value.completionMode === "resume_parent_on_each_terminal") context.addIssue({
		code: "custom",
		message: "Fallback activation does not support resume_parent_on_each_terminal.",
		path: ["completionMode"]
	});
});
const centerAgentWakePublicationCommandSchema = strictObject({
	activationTargets: array(agentProfileIdSchema).min(1),
	attribution: centerAgentAttributionSchema,
	clientMessageId: trimmedNonEmptyStringSchema,
	completionMode: centerAgentDelegationCompletionModeSchema,
	context: openMeldRequestContextSchema,
	delegationId: trimmedNonEmptyStringSchema,
	fallbackAgentProfileId: agentProfileIdSchema.nullable(),
	organizationId: trimmedNonEmptyStringSchema,
	parentSubmissionId: trimmedNonEmptyStringSchema,
	profile: profileSchema,
	mentionTargets: array(openMeldRuntimeProfileIdSchema).max(25).default([]),
	referenceTargets: array(openMeldRuntimeProfileIdSchema).max(25).default([]),
	spaceAddressingVersion: literal("1").optional(),
	spaceId: trimmedNonEmptyStringSchema,
	subscriptionIds: array(trimmedNonEmptyStringSchema).min(1),
	text: string().trim().min(1).max(MAX_TEXT_LENGTH),
	v: literal(1)
}).superRefine((value, context) => {
	const overlappingReferenceIndex = findCenterAgentReferenceOverlapIndex({
		activationTargets: value.activationTargets,
		fallbackAgentProfileId: value.fallbackAgentProfileId,
		referenceTargets: value.referenceTargets
	});
	if (overlappingReferenceIndex !== void 0 && overlappingReferenceIndex >= 0) context.addIssue({
		code: "custom",
		message: CENTER_AGENT_REFERENCE_OVERLAP_MESSAGE,
		path: ["referenceTargets", overlappingReferenceIndex]
	});
});
const centerAgentWatchAgentTaskBodySchema = strictObject({
	speakerMember: centerAgentToolSpeakerSchema.optional(),
	completionCondition: agentTaskCompletionConditionSchema,
	instruction: string().trim().min(1).max(4e3),
	task: agentTaskHandleSchema
});
const centerAgentListAgentTasksBodySchema = strictObject({
	speakerMember: centerAgentToolSpeakerSchema.optional(),
	agentControllerRef: agentControllerRefSchema.optional(),
	limit: int().min(1).max(20).optional()
});
const centerAgentUpdateSpaceSettingsBodySchema = strictObject({
	accessMode: spaceAccessModeSchema.optional(),
	guide: string().optional(),
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional(),
	password: spacePasswordSchema.optional(),
	purpose: string().optional(),
	spaceId: trimmedNonEmptyStringSchema
}).superRefine((value, ctx) => {
	if (value.accessMode === void 0 && value.guide === void 0 && value.purpose === void 0) ctx.addIssue({
		code: "custom",
		message: "Expected purpose, guide, or accessMode"
	});
	if (value.password !== void 0 && value.accessMode === void 0) ctx.addIssue({
		code: "custom",
		message: "password can only be set with accessMode",
		path: ["password"]
	});
	if (value.password !== void 0 && value.accessMode === "public") ctx.addIssue({
		code: "custom",
		message: "public access does not use a Space password",
		path: ["password"]
	});
});
const centerAgentInviteMemberBodySchema = strictObject({
	email: email(),
	role: enumFactory(["admin", "member"]).default("member"),
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional()
});
const centerAgentProductOperationStepNameSchema = enumFactory([
	"list_local_agent_connection_candidates",
	"select_local_agent_connection_candidate",
	"create_agent_profile",
	"enable_agent_profile",
	"connect_agent_profile_to_local_agent",
	"authorize_agent_profile_for_organization",
	"update_agent_profile_runtime_settings",
	"update_agent_profile_workspace",
	"add_agent_profile_to_space",
	"check_agent_profile_readiness"
]);
const centerAgentProductOperationStepSchema = strictObject({
	name: centerAgentProductOperationStepNameSchema,
	status: enumFactory([
		"completed",
		"failed",
		"skipped"
	]),
	message: trimmedNonEmptyStringSchema.optional(),
	nextAction: trimmedNonEmptyStringSchema.optional()
});
const centerAgentLocalAgentConnectionCandidateSchema = strictObject({
	agentControllerRef: trimmedNonEmptyStringSchema,
	agentDisplayName: trimmedNonEmptyStringSchema,
	candidateId: trimmedNonEmptyStringSchema,
	connectable: boolean(),
	deviceDisplayName: trimmedNonEmptyStringSchema,
	deviceId: trimmedNonEmptyStringSchema,
	freshness: enumFactory([
		"fresh",
		"stale",
		"unknown"
	]),
	lastObservedAtMs: number().int().nonnegative().nullable(),
	reasonCodes: array(trimmedNonEmptyStringSchema),
	runtimeLane: trimmedNonEmptyStringSchema,
	status: enumFactory([
		"ready",
		"not_ready",
		"stale",
		"unknown"
	]),
	openMeldServiceRuntimeId: prepareReadinessRuntimeIdSchema
});
const centerAgentComputerSetupOptionSchema = strictObject({
	connectedComputerId: trimmedNonEmptyStringSchema.nullable(),
	deviceDisplayName: trimmedNonEmptyStringSchema,
	deviceId: trimmedNonEmptyStringSchema,
	isCurrent: boolean(),
	lastObservedAtMs: number().int().nonnegative().nullable(),
	reasonCodes: array(trimmedNonEmptyStringSchema),
	recommendedMode: computerSetupModeSchema.nullable(),
	status: enumFactory([
		"connected",
		"needs_attention",
		"not_connected",
		"unknown"
	])
}).superRefine((value, context) => {
	if (value.recommendedMode !== null && value.recommendedMode !== "setup" && value.connectedComputerId === null) context.addIssue({
		code: "custom",
		message: "connectedComputerId is required for reconnect and update_service",
		path: ["connectedComputerId"]
	});
});
const centerAgentListLocalAgentConnectionCandidatesBodySchema = strictObject({
	memberUserId: trimmedNonEmptyStringSchema.optional(),
	speakerMember: centerAgentToolSpeakerSchema
});
const centerAgentListLocalAgentConnectionCandidatesResponseSchema = strictObject({
	candidates: array(centerAgentLocalAgentConnectionCandidateSchema),
	organizationId: trimmedNonEmptyStringSchema
});
const centerAgentListComputersBodySchema = strictObject({
	includeDiagnostics: boolean().optional(),
	includeUsageLimits: literal("true").optional(),
	speakerMember: centerAgentToolSpeakerSchema
});
const centerAgentListComputersResponseSchema = strictObject({
	computers: array(centerAgentComputerSetupOptionSchema),
	diagnostics: ownerComputerDiagnosticsSchema.optional(),
	organizationId: trimmedNonEmptyStringSchema
});
const centerAgentReadPeopleComputersBodySchema = strictObject({
	includeUsageLimits: literal("true").optional(),
	speakerMember: centerAgentToolSpeakerSchema,
	target: peopleComputerTargetSchema.optional()
});
const centerAgentReadPeopleComputersResponseSchema = lazySchema(() => strictObject({
	inventory: peopleComputersResponseSchema.extend({ computers: array(peopleComputersResponseSchema.shape.computers.element.extend({ executionTargets: array(peopleComputersResponseSchema.shape.computers.element.shape.executionTargets.element.omit({
		capabilities: true,
		localAgentCatalog: true
	})) })) }),
	organizationId: trimmedNonEmptyStringSchema,
	selectedComputer: strictObject({
		target: peopleComputerTargetSchema,
		readiness: currentLocalRuntimeReplyReadinessResponseSchema,
		fileAccess: computerFileAccessSummarySchema,
		care: computerCareObservationSchema.optional(),
		localRuntimes: ownerComputerLocalRuntimesSchema.optional()
	}).nullable()
}));
const centerAgentRequestOpenMeldToolsUpdateBodySchema = strictObject({
	speakerMember: centerAgentToolSpeakerSchema,
	target: peopleComputerTargetSchema.optional()
});
const centerAgentRequestOpenMeldToolsUpdateResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	result: requestCurrentOpenMeldToolsUpdateResponseSchema
});
const centerAgentSetOpenMeldToolsMaintenanceBodySchema = strictObject({
	automaticUpdatesEnabled: boolean(),
	speakerMember: centerAgentToolSpeakerSchema,
	target: peopleComputerTargetSchema.optional()
});
const centerAgentSetOpenMeldToolsMaintenanceResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	result: setCurrentOpenMeldToolsMaintenanceResponseSchema
});
const centerAgentListLocalAgentInstallationsBodySchema = strictObject({
	agentId: trimmedNonEmptyStringSchema.max(256),
	speakerMember: centerAgentToolSpeakerSchema,
	target: peopleComputerTargetSchema.optional()
});
const centerAgentListLocalAgentInstallationsResponseSchema = currentLocalAgentInstallationsResponseSchema.extend({ organizationId: trimmedNonEmptyStringSchema });
const centerAgentSelectLocalAgentInstallationBodySchema = strictObject({
	agentId: trimmedNonEmptyStringSchema.max(256),
	confirmed: literal(true),
	installationId: trimmedNonEmptyStringSchema.max(256),
	speakerMember: centerAgentToolSpeakerSchema,
	target: peopleComputerTargetSchema.optional()
});
const centerAgentSelectLocalAgentInstallationResponseSchema = selectCurrentLocalAgentInstallationResponseSchema.extend({ organizationId: trimmedNonEmptyStringSchema });
const centerAgentReadMemberComputersBodySchema = strictObject({
	includeUsageLimits: literal("true").optional(),
	memberUserId: trimmedNonEmptyStringSchema
});
const centerAgentReadMemberComputersResponseSchema = organizationComputersResponseSchema.extend({ memberUserId: trimmedNonEmptyStringSchema }).superRefine((value, context) => {
	value.computers.forEach((computer, index) => {
		if (computer.ownerUserId !== value.memberUserId) context.addIssue({
			code: "custom",
			message: "computer ownerUserId must match memberUserId",
			path: [
				"computers",
				index,
				"ownerUserId"
			]
		});
	});
});
const centerAgentConnectAgentProfileToLocalAgentBodySchema = strictObject({
	agentControllerCollaborationMode: agentProfileBindingAgentControllerCollaborationModeSchema.optional(),
	agentControllerPermissionMode: agentProfileBindingAgentControllerPermissionModeSchema.optional(),
	agentProfileId: agentProfileIdSchema,
	candidateId: trimmedNonEmptyStringSchema,
	modelId: trimmedNonEmptyStringSchema.optional(),
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional(),
	openCodePermission: agentProfileBindingOpenCodePermissionSchema.optional(),
	reasoningLevel: trimmedNonEmptyStringSchema.optional()
});
const centerAgentUpdateAgentProfileRuntimeSettingsBodySchema = strictObject({
	agentControllerCollaborationMode: agentProfileBindingAgentControllerCollaborationModeSchema.optional(),
	agentControllerPermissionMode: agentProfileBindingAgentControllerPermissionModeSchema.optional(),
	agentProfileId: agentProfileIdSchema,
	modelId: trimmedNonEmptyStringSchema.optional(),
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional(),
	openCodePermission: agentProfileBindingOpenCodePermissionSchema.optional(),
	reasoningLevel: trimmedNonEmptyStringSchema.optional(),
	workspaceMode: profileWorkspaceModeSchema.optional(),
	workspacePath: profileWorkingDirectorySchema.optional()
}).superRefine((value, ctx) => {
	if (value.agentControllerCollaborationMode === void 0 && value.agentControllerPermissionMode === void 0 && value.modelId === void 0 && value.openCodePermission === void 0 && value.reasoningLevel === void 0 && value.workspaceMode === void 0 && value.workspacePath === void 0) ctx.addIssue({
		code: "custom",
		message: "Expected at least one runtime setting"
	});
	if (value.reasoningLevel !== void 0 && value.modelId === void 0) ctx.addIssue({
		code: "custom",
		message: "reasoningLevel requires modelId",
		path: ["reasoningLevel"]
	});
	if (value.workspacePath !== void 0 && value.workspaceMode === void 0) ctx.addIssue({
		code: "custom",
		message: "workspacePath requires workspaceMode",
		path: ["workspacePath"]
	});
});
const centerAgentCheckAgentProfileReadinessBodySchema = strictObject({
	agentProfileId: agentProfileIdSchema,
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional()
});
const centerAgentListAgentProfileCreationDefaultsBodySchema = strictObject({ onBehalfOf: centerAgentOnBehalfOfInputSchema.optional() });
const centerAgentListAgentProfileCreationDefaultsResponseSchema = listAgentProfileCreationDefaultsResponseSchema;
const centerAgentCreateConfiguredAgentProfileBodySchema = strictObject({
	agentControllerCollaborationMode: agentProfileBindingAgentControllerCollaborationModeSchema.optional(),
	agentControllerPermissionMode: agentProfileBindingAgentControllerPermissionModeSchema.optional(),
	candidateId: trimmedNonEmptyStringSchema,
	configuration: agentProfileCreationPreferenceSelectionSchema.optional(),
	modelId: trimmedNonEmptyStringSchema.optional(),
	onBehalfOf: centerAgentOnBehalfOfInputSchema.optional(),
	openCodePermission: agentProfileBindingOpenCodePermissionSchema.optional(),
	profileName: trimmedNonEmptyStringSchema,
	reasoningLevel: trimmedNonEmptyStringSchema.optional(),
	spaceId: trimmedNonEmptyStringSchema,
	workspaceMode: profileWorkspaceModeSchema.optional(),
	workspacePath: profileWorkingDirectorySchema.optional()
}).superRefine((value, ctx) => {
	if (value.reasoningLevel !== void 0 && value.modelId === void 0) ctx.addIssue({
		code: "custom",
		message: "reasoningLevel requires modelId",
		path: ["reasoningLevel"]
	});
	if (value.workspacePath !== void 0 && value.workspaceMode === void 0) ctx.addIssue({
		code: "custom",
		message: "workspacePath requires workspaceMode",
		path: ["workspacePath"]
	});
});
const centerAgentReadinessBlockerSchema = strictObject({
	reasonCode: trimmedNonEmptyStringSchema,
	explanation: trimmedNonEmptyStringSchema
});
const centerAgentCheckAgentProfileReadinessResponseSchema = strictObject({
	agentProfileId: agentProfileIdSchema,
	blockers: array(centerAgentReadinessBlockerSchema),
	canReply: boolean(),
	organizationId: trimmedNonEmptyStringSchema,
	readiness: getAgentExecutionReadinessResponseSchema,
	summary: trimmedNonEmptyStringSchema
});
const centerAgentProductOperationStatusSchema = enumFactory(["completed", "blocked"]);
const centerAgentConnectAgentProfileToLocalAgentResponseSchema = strictObject({
	agentProfileId: agentProfileIdSchema,
	binding: ensureAgentProfilePrimaryBindingResponseSchema.optional(),
	canReply: boolean(),
	candidate: centerAgentLocalAgentConnectionCandidateSchema.optional(),
	organizationId: trimmedNonEmptyStringSchema,
	readiness: centerAgentCheckAgentProfileReadinessResponseSchema.optional(),
	status: centerAgentProductOperationStatusSchema,
	steps: array(centerAgentProductOperationStepSchema)
});
const centerAgentUpdateAgentProfileRuntimeSettingsResponseSchema = strictObject({
	agentProfileId: agentProfileIdSchema,
	binding: ensureAgentProfilePrimaryBindingResponseSchema.optional(),
	canReply: boolean(),
	organizationId: trimmedNonEmptyStringSchema,
	readiness: centerAgentCheckAgentProfileReadinessResponseSchema.optional(),
	status: centerAgentProductOperationStatusSchema,
	steps: array(centerAgentProductOperationStepSchema),
	workspace: setCurrentAgentProfileWorkspaceResponseSchema.optional()
});
const centerAgentCreateConfiguredAgentProfileResponseSchema = strictObject({
	agentProfileId: agentProfileIdSchema.optional(),
	canReply: boolean(),
	connection: centerAgentConnectAgentProfileToLocalAgentResponseSchema.optional(),
	organizationId: trimmedNonEmptyStringSchema,
	profile: authProfileViewSchema.optional(),
	readiness: centerAgentCheckAgentProfileReadinessResponseSchema.optional(),
	spaceId: trimmedNonEmptyStringSchema,
	status: centerAgentProductOperationStatusSchema,
	steps: array(centerAgentProductOperationStepSchema),
	workspace: setCurrentAgentProfileWorkspaceResponseSchema.optional()
});
const centerAgentConciergeActionResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	attribution: centerAgentAttributionSchema,
	resource: centerAgentResourceSchema.nullable()
});
const centerAgentCreateSpaceResponseSchema = centerAgentConciergeActionResponseSchema.extend({
	meta: spaceMetaSchema.optional(),
	spaceId: trimmedNonEmptyStringSchema
});
const centerAgentCreateAgentProfileResponseSchema = centerAgentConciergeActionResponseSchema.extend({ profile: authProfileViewSchema });
const centerAgentEnableAgentProfileResponseSchema = centerAgentConciergeActionResponseSchema.extend({
	agentProfileId: agentProfileIdSchema,
	enabled: boolean()
});
const centerAgentAddAgentProfilesToSpaceResponseSchema = centerAgentConciergeActionResponseSchema.extend({
	agentProfileIds: array(agentProfileIdSchema),
	spaceId: trimmedNonEmptyStringSchema,
	membership: postSpaceMembersResponseSchema
});
const centerAgentAddMembersToSpaceResultSchema = discriminatedUnion("status", [strictObject({
	memberUserId: trimmedNonEmptyStringSchema,
	profileId: openMeldRuntimeProfileIdSchema,
	status: enumFactory(["added", "already_member"])
}), strictObject({
	code: enumFactory(["not_organization_member", "active_human_profile_required"]),
	memberUserId: trimmedNonEmptyStringSchema,
	message: trimmedNonEmptyStringSchema,
	status: literal("rejected")
})]);
const centerAgentAddMembersToSpaceResponseSchema = centerAgentConciergeActionResponseSchema.extend({
	membership: postSpaceMembersResponseSchema,
	results: array(centerAgentAddMembersToSpaceResultSchema),
	spaceId: trimmedNonEmptyStringSchema
});
const centerAgentRemoveAgentProfilesFromSpaceResponseSchema = centerAgentConciergeActionResponseSchema.extend({
	agentProfileIds: array(agentProfileIdSchema),
	spaceId: trimmedNonEmptyStringSchema,
	membership: removeSpaceMembersResponseSchema
});
const centerAgentSpaceContextBaseResponseShape = {
	organizationId: trimmedNonEmptyStringSchema,
	spaceId: trimmedNonEmptyStringSchema
};
const centerAgentProgressiveSpaceContextSchema = discriminatedUnion("scope", [
	strictObject({
		scope: literal("source"),
		signals: array(signalEnvelopeSchema).max(1),
		sourceSignalId: trimmedNonEmptyStringSchema,
		threadContext: union([spaceThreadContextSchema, nullFactory()]),
		truncated: literal(false)
	}),
	strictObject({
		scope: literal("thread"),
		signals: array(signalEnvelopeSchema).max(100),
		threadId: trimmedNonEmptyStringSchema,
		truncated: boolean()
	}),
	strictObject({
		afterSignalId: trimmedNonEmptyStringSchema,
		scope: literal("delta"),
		signals: array(signalEnvelopeSchema).max(100),
		truncated: boolean()
	}),
	strictObject({
		scope: literal("history"),
		signals: array(signalEnvelopeSchema).max(100),
		truncated: boolean()
	})
]);
const centerAgentReadSpaceContextResponseSchema = union([strictObject({
	...centerAgentSpaceContextBaseResponseShape,
	bootstrap: spaceBootstrapResponseSchema
}), strictObject({
	...centerAgentSpaceContextBaseResponseShape,
	context: centerAgentProgressiveSpaceContextSchema
})]);
const centerAgentDispatchHistoryLatestAttemptSchema = strictObject({
	attemptId: trimmedNonEmptyStringSchema,
	attemptNo: int().min(1),
	createdAt: ISO_DATE_TIME_SCHEMA,
	errorCode: nullableTrimmedStringSchema,
	errorMessage: nullableDiagnosticTextSchema,
	finishedAt: nullableIsoDateTimeSchema,
	startedAt: nullableIsoDateTimeSchema,
	status: spaceDispatchTraceAttemptStatusSchema
});
const centerAgentDispatchHistoryEntrySchema = strictObject({
	attemptCount: int().min(0),
	dispatchId: trimmedNonEmptyStringSchema,
	firstSeenAt: ISO_DATE_TIME_SCHEMA,
	lastAttemptAt: ISO_DATE_TIME_SCHEMA,
	latestAttempt: union([centerAgentDispatchHistoryLatestAttemptSchema, nullFactory()]),
	status: spaceDispatchTraceDispatchStatusSchema,
	targetProfileId: trimmedNonEmptyStringSchema
});
const centerAgentReadDispatchHistoryResponseSchema = strictObject({
	entries: array(centerAgentDispatchHistoryEntrySchema),
	organizationId: trimmedNonEmptyStringSchema,
	spaceId: trimmedNonEmptyStringSchema
});
const centerAgentUpdateSpaceSettingsResponseSchema = centerAgentConciergeActionResponseSchema.extend({
	accessModeState: spaceAccessModeStateSchema.optional(),
	meta: spaceMetaSchema.optional(),
	spaceId: trimmedNonEmptyStringSchema
});
const centerAgentSpaceAuthoringContractResponseSchema = discriminatedUnion("addressingModel", [strictObject({
	addressingModel: literal("wake_reference"),
	structuredReferenceTargets: literal(true)
}), strictObject({
	addressingModel: literal("wake_reference_mention"),
	structuredMentionTargets: literal(true),
	structuredReferenceTargets: literal(true)
})]);
const centerAgentPostSpaceMessageResponseSchema = centerAgentConciergeActionResponseSchema.extend({
	signal: signalTextEnvelopeSchema,
	spaceId: trimmedNonEmptyStringSchema
});
const centerAgentPostSlackChannelMessageResponseSchema = strictObject({
	attribution: centerAgentAttributionSchema,
	channelName: trimmedNonEmptyStringSchema.max(80),
	joinedChannel: boolean(),
	messageTs: trimmedNonEmptyStringSchema,
	organizationId: trimmedNonEmptyStringSchema
});
const centerAgentPostSlackFileResponseSchema = strictObject({
	attribution: centerAgentAttributionSchema,
	channelName: trimmedNonEmptyStringSchema.max(80),
	file: slackFileMetadataSchema,
	joinedChannel: boolean(),
	organizationId: trimmedNonEmptyStringSchema
});
const centerAgentSlackSearchFailureSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	reason: trimmedNonEmptyStringSchema.max(500),
	retryAfterSeconds: number().int().nonnegative().optional(),
	status: enumFactory([
		"authorization_expired",
		"forbidden",
		"rate_limited",
		"unavailable"
	])
});
const CENTER_AGENT_SLACK_SEARCH_CONTENT_MAX_CHARS = 2e3;
const CENTER_AGENT_SLACK_SEARCH_CONTEXT_MESSAGES_MAX = 2;
const CENTER_AGENT_SLACK_SEARCH_MESSAGES_MAX = 8;
const centerAgentSlackSearchEvidenceItemSchema = strictObject({
	authorName: trimmedNonEmptyStringSchema.max(256),
	authorUserId: trimmedNonEmptyStringSchema.max(256),
	channelId: trimmedNonEmptyStringSchema.max(256),
	channelName: trimmedNonEmptyStringSchema.max(256),
	content: trimmedNonEmptyStringSchema.max(CENTER_AGENT_SLACK_SEARCH_CONTENT_MAX_CHARS),
	isAuthorBot: boolean(),
	messageTs: trimmedNonEmptyStringSchema.max(64),
	permalink: slackMessagePermalinkSchema
});
const centerAgentSlackSearchMessageSchema = centerAgentSlackSearchEvidenceItemSchema.extend({ contextMessages: array(centerAgentSlackSearchEvidenceItemSchema).max(2) });
const centerAgentSearchSlackMessagesResponseSchema = discriminatedUnion("status", [strictObject({
	messages: array(centerAgentSlackSearchMessageSchema).max(8),
	moreResultsAvailable: boolean(),
	organizationId: trimmedNonEmptyStringSchema,
	status: literal("found")
}), centerAgentSlackSearchFailureSchema]);
const centerAgentWakeAgentProfilesInSpaceResponseBaseSchema = centerAgentConciergeActionResponseSchema.extend({
	agentProfileIds: array(agentProfileIdSchema),
	childTasks: array(strictObject({
		completionCondition: literal("terminal"),
		subscriptionId: trimmedNonEmptyStringSchema,
		task: spaceWakeAgentTaskHandleSchema
	})).optional(),
	delegationId: trimmedNonEmptyStringSchema.optional(),
	fallbackTask: strictObject({
		activationSubscriptionId: trimmedNonEmptyStringSchema,
		completionCondition: literal("terminal"),
		subscriptionId: trimmedNonEmptyStringSchema,
		task: spaceWakeAgentTaskHandleSchema
	}).optional(),
	spaceId: trimmedNonEmptyStringSchema
});
const centerAgentWakeAgentProfilesInSpaceResponseSchema = union([centerAgentWakeAgentProfilesInSpaceResponseBaseSchema.extend({
	publicationStatus: literal("accepted").optional(),
	signal: signalTextEnvelopeSchema
}), centerAgentWakeAgentProfilesInSpaceResponseBaseSchema.extend({
	publicationStatus: literal("pending"),
	retryAfterSeconds: int().positive().max(300)
}).superRefine((value, context) => {
	if (!(value.delegationId && value.childTasks)) context.addIssue({
		code: "custom",
		message: "a pending Wake publication requires its durable delegation contract",
		path: ["delegationId"]
	});
})]).superRefine((value, context) => {
	if (value.delegationId === void 0 !== (value.childTasks === void 0)) context.addIssue({
		code: "custom",
		message: "delegationId and childTasks must either both be present or both be absent",
		path: ["delegationId"]
	});
	if (value.fallbackTask && !value.delegationId) context.addIssue({
		code: "custom",
		message: "fallbackTask requires a delegationId",
		path: ["fallbackTask"]
	});
	if (value.childTasks) {
		const expectedProfileIds = [...new Set(value.agentProfileIds)].sort();
		const childProfileIds = value.childTasks.map((child) => child.task.targetProfileId).sort();
		if (childProfileIds.length !== expectedProfileIds.length || childProfileIds.some((profileId, index) => profileId !== expectedProfileIds[index])) context.addIssue({
			code: "custom",
			message: "childTasks must contain exactly one Space Wake task for every agentProfileId",
			path: ["childTasks"]
		});
		if (value.fallbackTask && expectedProfileIds.includes(value.fallbackTask.task.targetProfileId)) context.addIssue({
			code: "custom",
			message: "fallbackTask must target a different Agent Profile",
			path: ["fallbackTask"]
		});
	}
});
const centerAgentWatchAgentTaskResponseSchema = centerAgentConciergeActionResponseSchema.extend({
	completionCondition: agentTaskCompletionConditionSchema,
	status: enumFactory([
		"watching",
		"continuation_pending",
		"continuation_created",
		"condition_not_met"
	]),
	outcome: union([agentTaskCompletionObservationSchema, nullFactory()]),
	subscriptionId: trimmedNonEmptyStringSchema,
	task: agentTaskHandleSchema,
	watchId: trimmedNonEmptyStringSchema
});
const centerAgentListAgentTaskSchema = strictObject({
	outcome: union([agentTaskTerminalOutcomeSchema, nullFactory()]),
	registeredAtMs: int().nonnegative(),
	registrationSource: agentControllerTaskLifecycleEventSourceSchema,
	supportedCompletionConditions: array(agentTaskCompletionConditionSchema),
	task: controllerAgentTaskHandleSchema
});
const centerAgentListAgentTasksResponseSchema = strictObject({
	hasMore: boolean(),
	organizationId: trimmedNonEmptyStringSchema,
	tasks: array(centerAgentListAgentTaskSchema).max(20)
});
const centerAgentInviteMemberResponseSchema = centerAgentConciergeActionResponseSchema.extend({ invited: literal(true) });
const openCenterAgentDirectMessageSpaceResponseSchema = strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	centerAgentProfileId: agentProfileIdSchema,
	memberProfileId: openMeldRuntimeProfileIdSchema,
	spaceId: trimmedNonEmptyStringSchema,
	created: boolean()
});
//#endregion
export { CENTER_AGENT_DELEGATION_COMPLETION_MODES, CENTER_AGENT_PUBLICATION_OPERATION_ID_MAX_CHARS, CENTER_AGENT_SLACK_SEARCH_CONTENT_MAX_CHARS, CENTER_AGENT_SLACK_SEARCH_CONTEXT_MESSAGES_MAX, CENTER_AGENT_SLACK_SEARCH_MESSAGES_MAX, centerAgentActionSchema, centerAgentAddAgentProfilesToSpaceBodySchema, centerAgentAddAgentProfilesToSpaceResponseSchema, centerAgentAddMembersToSpaceBodySchema, centerAgentAddMembersToSpaceResponseSchema, centerAgentAddMembersToSpaceResultSchema, centerAgentAttributionSchema, centerAgentAuditJournalEntrySchema, centerAgentCheckAgentProfileReadinessBodySchema, centerAgentCheckAgentProfileReadinessResponseSchema, centerAgentComputerSetupOptionSchema, centerAgentConciergeActionResponseSchema, centerAgentConnectAgentProfileToLocalAgentBodySchema, centerAgentConnectAgentProfileToLocalAgentResponseSchema, centerAgentConnectorActionAuditOutcomeSchema, centerAgentConnectorActionExecutionConnectionSchema, centerAgentConnectorActionOperationStatusSchema, centerAgentCreateAgentProfileBodySchema, centerAgentCreateAgentProfileResponseSchema, centerAgentCreateConfiguredAgentProfileBodySchema, centerAgentCreateConfiguredAgentProfileResponseSchema, centerAgentCreateSpaceBodySchema, centerAgentCreateSpaceResponseSchema, centerAgentDelegationCompletionModeSchema, centerAgentDesignationSchema, centerAgentDispatchHistoryEntrySchema, centerAgentDispatchHistoryLatestAttemptSchema, centerAgentEnableAgentProfileBodySchema, centerAgentEnableAgentProfileResponseSchema, centerAgentInviteMemberBodySchema, centerAgentInviteMemberResponseSchema, centerAgentListAgentProfileCreationDefaultsBodySchema, centerAgentListAgentProfileCreationDefaultsResponseSchema, centerAgentListAgentTaskSchema, centerAgentListAgentTasksBodySchema, centerAgentListAgentTasksResponseSchema, centerAgentListComputersBodySchema, centerAgentListComputersResponseSchema, centerAgentListLocalAgentConnectionCandidatesBodySchema, centerAgentListLocalAgentConnectionCandidatesResponseSchema, centerAgentListLocalAgentInstallationsBodySchema, centerAgentListLocalAgentInstallationsResponseSchema, centerAgentLocalAgentConnectionCandidateSchema, centerAgentModelConfigurationResponseSchema, centerAgentOnBehalfOfInputSchema, centerAgentOperatingStateSchema, centerAgentPostSlackChannelMessageBodySchema, centerAgentPostSlackChannelMessageResponseSchema, centerAgentPostSlackFileBodySchema, centerAgentPostSlackFileResponseSchema, centerAgentPostSlackThreadMessageBodySchema, centerAgentPostSpaceMessageBodySchema, centerAgentPostSpaceMessageResponseSchema, centerAgentProductOperationStepNameSchema, centerAgentProductOperationStepSchema, centerAgentPublicationSourceSchema, centerAgentReadConnectorActionOperationBodySchema, centerAgentReadConnectorActionOperationResponseSchema, centerAgentReadDispatchHistoryBodySchema, centerAgentReadDispatchHistoryResponseSchema, centerAgentReadMemberComputersBodySchema, centerAgentReadMemberComputersResponseSchema, centerAgentReadPeopleComputersBodySchema, centerAgentReadPeopleComputersResponseSchema, centerAgentReadSpaceContextBodySchema, centerAgentReadSpaceContextResponseSchema, centerAgentReadinessBlockerSchema, centerAgentRecordConnectorActionAuditBodySchema, centerAgentRecordConnectorActionAuditResponseSchema, centerAgentRecordConnectorActionOperationBodySchema, centerAgentRecordConnectorActionOperationResponseSchema, centerAgentRemoteRuntimeAvailabilityReasonCodeSchema, centerAgentRemoteRuntimeAvailabilitySchema, centerAgentRemoveAgentProfilesFromSpaceBodySchema, centerAgentRemoveAgentProfilesFromSpaceResponseSchema, centerAgentRequestOpenMeldToolsUpdateBodySchema, centerAgentRequestOpenMeldToolsUpdateResponseSchema, centerAgentResourceSchema, centerAgentSearchSlackMessagesBodySchema, centerAgentSearchSlackMessagesResponseSchema, centerAgentSelectLocalAgentInstallationBodySchema, centerAgentSelectLocalAgentInstallationResponseSchema, centerAgentSetOpenMeldToolsMaintenanceBodySchema, centerAgentSetOpenMeldToolsMaintenanceResponseSchema, centerAgentSlackSearchMessageSchema, centerAgentSlackWorkspaceConnectBodySchema, centerAgentSlackWorkspaceConnectionResponseSchema, centerAgentSlackWorkspaceConnectionSchema, centerAgentSlackWorkspaceDisconnectResponseSchema, centerAgentSpaceAuthoringContractResponseSchema, centerAgentSpaceContextScopeSchema, centerAgentUpdateAgentProfileRuntimeSettingsBodySchema, centerAgentUpdateAgentProfileRuntimeSettingsResponseSchema, centerAgentUpdateProfileBodySchema, centerAgentUpdateProfileResponseSchema, centerAgentUpdateSpaceSettingsBodySchema, centerAgentUpdateSpaceSettingsResponseSchema, centerAgentWakeAgentProfilesInSpaceBodySchema, centerAgentWakeAgentProfilesInSpaceResponseSchema, centerAgentWakePublicationCommandSchema, centerAgentWatchAgentTaskBodySchema, centerAgentWatchAgentTaskResponseSchema, designateCenterAgentBodySchema, designateCenterAgentResponseSchema, ensureMessageChannelMemberOrganizationMembershipRpcInputSchema, ensureMessageChannelMemberOrganizationMembershipRpcResponseSchema, ensureSlackWorkspaceMemberOrganizationMembershipRpcInputSchema, ensureSlackWorkspaceMemberOrganizationMembershipRpcResponseSchema, getCenterAgentDesignationResponseSchema, getCenterAgentOperatingStateResponseSchema, listCenterAgentAuditJournalQuerySchema, listCenterAgentAuditJournalResponseSchema, openCenterAgentDirectMessageSpaceResponseSchema, slackIdentityLinkConsumeBodySchema, slackIdentityLinkConsumeResponseSchema, slackIdentityLinkPreviewBodySchema, slackIdentityLinkPreviewResponseSchema, slackIdentityLinkTokenSchema, updateCenterAgentModelBodySchema, updateCenterAgentModelResponseSchema, updateCenterAgentOperatingStateBodySchema, updateCenterAgentOperatingStateResponseSchema };

//# sourceMappingURL=center-agent.js.map